/**
 * <cart-drawer>
 *
 * Slide-in cart drawer that auto-opens after add-to-cart, supports
 * inline quantity changes and removals, and uses the section rendering
 * API to keep its contents in sync without full page reloads.
 *
 * Public events:
 *   document.dispatchEvent(new CustomEvent('cart:open'))     — open
 *   document.dispatchEvent(new CustomEvent('cart:close'))    — close
 *   document.dispatchEvent(new CustomEvent('cart:refresh'))  — refetch + re-render
 *   document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }))
 *     — emitted after every successful cart mutation
 */
(() => {
  const SECTION_ID = 'cart-drawer';
  const OPEN_CLASS = 'cart-drawer-open';

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-cart-drawer-overlay]');
      this.panel = this.querySelector('[data-cart-drawer-panel]');

      // Bind events even if children are missing — ESC + form intercept
      // need to keep working in case the inner markup ever drifts.
      this.bindEvents();
    }

    bindEvents() {
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-drawer-close]')) {
          e.preventDefault();
          this.close();
          return;
        }
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          const item = removeBtn.closest('[data-line-item]');
          this.changeLineFor(item, 0);
          return;
        }
        const dec = e.target.closest('[data-quantity-decrease]');
        if (dec) {
          e.preventDefault();
          const item = dec.closest('[data-line-item]');
          const input = item?.querySelector('[data-quantity-input]');
          if (!input) return;
          const next = Math.max(0, parseInt(input.value || '0', 10) - 1);
          this.changeLineFor(item, next);
          return;
        }
        const inc = e.target.closest('[data-quantity-increase]');
        if (inc) {
          e.preventDefault();
          const item = inc.closest('[data-line-item]');
          const input = item?.querySelector('[data-quantity-input]');
          if (!input) return;
          const next = parseInt(input.value || '0', 10) + 1;
          this.changeLineFor(item, next);
        }
      });

      // Cart note (debounced)
      this.addEventListener('input', (e) => {
        const note = e.target.closest('[data-cart-note]');
        if (!note) return;
        clearTimeout(this._noteTimer);
        this._noteTimer = setTimeout(() => this.updateNote(note.value), 400);
      });

      // Global open/close events
      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:close', () => this.close());
      document.addEventListener('cart:refresh', () => this.refresh());

      // ESC closes
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    }

    isOpen() {
      return this.getAttribute('aria-hidden') === 'false';
    }

    open() {
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add(OPEN_CLASS);
      document.body.classList.add(OPEN_CLASS);
      this._lastFocused = document.activeElement;
      requestAnimationFrame(() => {
        const closeBtn = this.querySelector('[data-cart-drawer-close]');
        if (closeBtn) closeBtn.focus();
      });
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove(OPEN_CLASS);
      document.body.classList.remove(OPEN_CLASS);
      if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
        this._lastFocused.focus();
      }
    }

    setLoading(loading) {
      this.toggleAttribute('data-loading', loading);
      this.dataset.loading = loading ? 'true' : 'false';
    }

    /**
     * Resolve a [data-line-item] element to the most reliable identifier
     * for /cart/change.js. Prefer the 1-indexed `line` (Shopify's most
     * stable selector), fall back to the line key.
     */
    changeLineFor(item, quantity) {
      if (!item) return;
      const lineIndex = parseInt(item.dataset.lineIndex || '', 10);
      if (Number.isInteger(lineIndex) && lineIndex > 0) {
        return this.changeLine({ line: lineIndex }, quantity);
      }
      const key = item.dataset.lineKey;
      if (key) return this.changeLine({ id: key }, quantity);
    }

    async changeLine(target, quantity) {
      this.setLoading(true);
      const body = { quantity, sections: SECTION_ID };
      if (target && typeof target === 'object') {
        Object.assign(body, target);
      } else if (target) {
        body.id = target;
      }
      try {
        const res = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));

        // Shopify returns 4xx with a "description" field when the change
        // can't be applied (e.g. quantity above available inventory).
        // Surface it instead of silently swallowing the failure.
        if (!res.ok || data.status >= 400) {
          const msg = data.description || data.message || 'Aktualisierung fehlgeschlagen.';
          console.warn('[cart-drawer] /cart/change.js rejected:', msg, body);
          this.flashError(msg);
          return;
        }

        if (data.sections && data.sections[SECTION_ID]) {
          this.applySection(data.sections[SECTION_ID]);
        } else {
          await this.refresh();
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
      } catch (err) {
        console.error('[cart-drawer] line change failed', err);
        this.flashError('Aktualisierung fehlgeschlagen.');
      } finally {
        this.setLoading(false);
      }
    }

    flashError(message) {
      let banner = this.querySelector('[data-cart-error]');
      if (!banner) {
        banner = document.createElement('div');
        banner.className = 'cart-drawer__error';
        banner.setAttribute('data-cart-error', '');
        banner.setAttribute('role', 'alert');
        const body = this.querySelector('[data-cart-drawer-body]');
        if (body) body.prepend(banner);
      }
      banner.textContent = message;
      clearTimeout(this._errorTimer);
      this._errorTimer = setTimeout(() => {
        banner?.remove();
      }, 4000);
    }

    async updateNote(note) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        });
      } catch (err) {
        console.error('[cart-drawer] note update failed', err);
      }
    }

    async refresh() {
      this.setLoading(true);
      try {
        const res = await fetch(`?section_id=${SECTION_ID}`);
        const html = await res.text();
        const tempDoc = new DOMParser().parseFromString(html, 'text/html');
        const newDrawer = tempDoc.querySelector('cart-drawer');
        if (newDrawer) this.applySection(newDrawer.outerHTML);
      } catch (err) {
        console.error('[cart-drawer] refresh failed', err);
      } finally {
        this.setLoading(false);
      }
    }

    applySection(html) {
      const tempDoc = new DOMParser().parseFromString(html, 'text/html');
      const newDrawer = tempDoc.querySelector('cart-drawer');
      if (!newDrawer) return;

      // Replace the panel element entirely (not just innerHTML) so we
      // pick up any class/attribute changes from the freshly rendered
      // markup. Use cloneNode + replaceWith instead of adoptNode so we
      // never accidentally move a node from the parser document.
      const newPanel = newDrawer.querySelector('[data-cart-drawer-panel]');
      const oldPanel = this.querySelector('[data-cart-drawer-panel]');
      if (newPanel && oldPanel && oldPanel.parentNode) {
        const clone = newPanel.cloneNode(true);
        oldPanel.replaceWith(clone);
        this.panel = clone;
      }

      // Sync count-bearing elements throughout the page.
      // Exclude the <cart-drawer> host: it carries data-cart-count as a
      // state attribute, and writing textContent on it would wipe the
      // entire drawer markup (overlay + panel) and leave only the count.
      const newCount = newDrawer.dataset.cartCount || '0';
      this.dataset.cartCount = newCount;
      document.querySelectorAll('[data-cart-count]:not(cart-drawer)').forEach((el) => {
        el.textContent = newCount;
      });

      // Toggle the header cart badge if it should now appear/disappear
      const cartLinks = document.querySelectorAll('[data-utility="cart"]');
      cartLinks.forEach((link) => {
        const badge = link.querySelector('.site-header__cart-badge');
        const count = parseInt(newCount, 10) || 0;
        if (count > 0 && !badge) {
          const span = document.createElement('span');
          span.className = 'site-header__cart-badge';
          span.setAttribute('aria-hidden', 'true');
          span.setAttribute('data-cart-count', '');
          span.textContent = newCount;
          link.appendChild(span);
        } else if (count <= 0 && badge) {
          badge.remove();
        }
      });
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
  }

  /* ----------------------------------------------------------------
   * Form interception — POST product forms via /cart/add.js and open
   * the drawer on success.
   * ---------------------------------------------------------------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;
    if (form.dataset.cartDrawerSkip === 'true') return;

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return; // Fall through to native submit

    e.preventDefault();

    const submitBtn = form.querySelector('[type="submit"]');
    const labelEl = submitBtn?.querySelector('[data-cta-label]');
    const originalLabel = labelEl?.textContent;
    if (submitBtn) submitBtn.disabled = true;

    try {
      const formData = new FormData(form);
      formData.append('sections', SECTION_ID);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/javascript' },
      });
      const data = await res.json();

      if (!res.ok || data.status >= 400) {
        const msg = data.description || data.message || 'Beim Hinzufügen ist ein Fehler aufgetreten.';
        if (labelEl) labelEl.textContent = msg;
        setTimeout(() => {
          if (labelEl && originalLabel) labelEl.textContent = originalLabel;
        }, 2500);
        return;
      }

      if (data.sections && data.sections[SECTION_ID]) {
        drawer.applySection(data.sections[SECTION_ID]);
      } else {
        await drawer.refresh();
      }
      drawer.open();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
    } catch (err) {
      console.error('[cart-drawer] add failed', err);
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });

  /* ----------------------------------------------------------------
   * Header cart icon — open the drawer instead of navigating to /cart
   * ---------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const cartLink = e.target.closest('a[data-utility="cart"]');
    if (!cartLink) return;
    if (cartLink.dataset.cartDrawerSkip === 'true') return;

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    e.preventDefault();
    drawer.open();
  });
})();
